// Runtime (Firestore-backed) feature flag system — distinct from the compile-time
// constants in ../config/featureFlags.ts (PLANS_ENABLED etc.), which require a code
// deploy to flip and have no per-org scope. This module backs toggles that Tanishk
// can flip live from the super-admin screen, with an optional per-org override.
//
// Data model (both reads are single-doc, no query — cheap on every page load):
//   featureFlags/global                 { flags: { [key]: FeatureFlagDef } }
//   featureFlagOverrides/{orgId}        { overrides: { [key]: boolean }, updatedAt, updatedBy }
//
// Precedence (see computeEffectiveFlag):
//   state 'off'     -> OFF for everyone, no override can turn it back on (hard kill switch).
//   state 'on'      -> ON for everyone, unless an org has an explicit override === false.
//   state 'rollout' -> OFF for everyone EXCEPT orgs with an explicit override === true
//                       (staged rollout: opt a single agency in before going platform-wide).
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteField,
  type Unsubscribe,
} from 'firebase/firestore';
import { ensureDb } from './firestore/ensureDb';
import { DRIVING_RECORD_FLAG, DRIVING_RECORD_FLAG_DESCRIPTION, DRIVING_RECORD_FLAG_LABEL } from './drivingRecord';
import {
  EMT_ONBOARDING_V2_FLAG,
  EMT_ONBOARDING_V2_FLAG_DESCRIPTION,
  EMT_ONBOARDING_V2_FLAG_LABEL,
} from './onboarding/emtSignupFlow';
import { EMT_READINESS_V2_FLAG, EMT_READINESS_V2_FLAG_DESCRIPTION, EMT_READINESS_V2_FLAG_LABEL } from './readiness/emtReadinessView';
import { EMT_READINESS_V3_FLAG, EMT_READINESS_V3_FLAG_DESCRIPTION, EMT_READINESS_V3_FLAG_LABEL } from './readiness/jobReadiness';
import { EMT_ASSISTANT_FLAG, EMT_ASSISTANT_FLAG_DESCRIPTION, EMT_ASSISTANT_FLAG_LABEL } from './assistant/emtAssistant';
import { SERVER_CANDIDATE_SEARCH_FLAG, SERVER_CANDIDATE_SEARCH_FLAG_DESCRIPTION, SERVER_CANDIDATE_SEARCH_FLAG_LABEL } from './search/candidateSearchFlag';
import { DOCUMENT_REQUESTS_FLAG, DOCUMENT_REQUESTS_FLAG_DESCRIPTION, DOCUMENT_REQUESTS_FLAG_LABEL } from './documents/documentRequests';
import { HR_SENSITIVE_HANDOFF_FLAG, HR_SENSITIVE_HANDOFF_FLAG_DESCRIPTION, HR_SENSITIVE_HANDOFF_FLAG_LABEL } from './hr/payrollHandoff';
import { TEAM_SCHEDULE_FLAG, TEAM_SCHEDULE_FLAG_DESCRIPTION, TEAM_SCHEDULE_FLAG_LABEL } from './scheduling/teamSchedule';
import { HIRE_CHECKLIST_FLAG, HIRE_CHECKLIST_FLAG_DESCRIPTION, HIRE_CHECKLIST_FLAG_LABEL } from './hiring/hireChecklist';
import { APPLICANT_PIPELINE_FLAG, APPLICANT_PIPELINE_FLAG_DESCRIPTION, APPLICANT_PIPELINE_FLAG_LABEL } from './applications/pipelineStages';
import { APPLICANT_VIEW_FLAG, APPLICANT_VIEW_FLAG_DESCRIPTION, APPLICANT_VIEW_FLAG_LABEL } from './applications/applicantFeedback';
import { SMS_FLAG_DESCRIPTION, SMS_FLAG_LABEL, SMS_NOTIFICATIONS_FLAG } from './sms/consent';
import { DRIVER_ROLES_FLAG, DRIVER_ROLES_FLAG_DESCRIPTION, DRIVER_ROLES_FLAG_LABEL } from './jobs/driverRoles';

export type FeatureFlagState = 'off' | 'on' | 'rollout';

export interface FeatureFlagDef {
  label: string;
  description?: string;
  state: FeatureFlagState;
  updatedAt?: unknown;
  updatedBy?: string;
}

export interface GlobalFeatureFlagsDoc {
  flags: Record<string, FeatureFlagDef>;
}

export interface OrgFeatureFlagOverridesDoc {
  overrides: Record<string, boolean>;
  updatedAt?: unknown;
  updatedBy?: string;
}

/**
 * Public careers page redesign (2026-10-09). featureFlags/global needs a signed-in reader, so for the
 * signed-out visitors of /careers/:orgId the server (functions/src/jobPageMeta.ts) resolves it for
 * the page's employer and marks the HTML; signed-in viewers also read it here.
 */
export const CAREERS_PAGE_V2_FLAG = 'careers_page_v2';

const GLOBAL_DOC_PATH = ['featureFlags', 'global'] as const;

/**
 * Flags the code reads that the super-admin page should offer even before they exist in
 * featureFlags/global (the page otherwise lists only what is stored). Listing one here creates
 * nothing: an absent flag is OFF everywhere, and the page's "Add (off)" button stores it as 'off'.
 */
export const KNOWN_FEATURE_FLAGS: ReadonlyArray<{ key: string; label: string; description: string }> = [
  { key: DRIVING_RECORD_FLAG, label: DRIVING_RECORD_FLAG_LABEL, description: DRIVING_RECORD_FLAG_DESCRIPTION },
  { key: EMT_ONBOARDING_V2_FLAG, label: EMT_ONBOARDING_V2_FLAG_LABEL, description: EMT_ONBOARDING_V2_FLAG_DESCRIPTION },
  { key: EMT_READINESS_V2_FLAG, label: EMT_READINESS_V2_FLAG_LABEL, description: EMT_READINESS_V2_FLAG_DESCRIPTION },
  { key: EMT_READINESS_V3_FLAG, label: EMT_READINESS_V3_FLAG_LABEL, description: EMT_READINESS_V3_FLAG_DESCRIPTION },
  { key: EMT_ASSISTANT_FLAG, label: EMT_ASSISTANT_FLAG_LABEL, description: EMT_ASSISTANT_FLAG_DESCRIPTION },
  { key: SERVER_CANDIDATE_SEARCH_FLAG, label: SERVER_CANDIDATE_SEARCH_FLAG_LABEL, description: SERVER_CANDIDATE_SEARCH_FLAG_DESCRIPTION },
  { key: HR_SENSITIVE_HANDOFF_FLAG, label: HR_SENSITIVE_HANDOFF_FLAG_LABEL, description: HR_SENSITIVE_HANDOFF_FLAG_DESCRIPTION },
  {
    key: 'team_expiry_digest',
    label: 'Weekly team credential digest (owners)',
    description:
      "Every Monday at 8 AM in the owner's time zone, each organization's owner gets one email and one in-app notice listing the team's credentials that are expired or expire within 90 days. Nothing is sent when there is nothing to report. Rollout: only the employers you opt in. Off: no digest (the Workforce card and the Credentials page still show expiry).",
  },
  { key: DOCUMENT_REQUESTS_FLAG, label: DOCUMENT_REQUESTS_FLAG_LABEL, description: DOCUMENT_REQUESTS_FLAG_DESCRIPTION },
  { key: APPLICANT_PIPELINE_FLAG, label: APPLICANT_PIPELINE_FLAG_LABEL, description: APPLICANT_PIPELINE_FLAG_DESCRIPTION },
  { key: APPLICANT_VIEW_FLAG, label: APPLICANT_VIEW_FLAG_LABEL, description: APPLICANT_VIEW_FLAG_DESCRIPTION },
  {
    key: CAREERS_PAGE_V2_FLAG,
    label: 'Careers page: grouped by job title',
    description:
      "The public careers page (/careers/:orgId) groups jobs by title with every location together, adds filters for title, location and pay, and shows the employer's brand color. Rollout: on for the employers you opt in, for every visitor of their page. Off: the current page.",
  },
  { key: SMS_NOTIFICATIONS_FLAG, label: SMS_FLAG_LABEL, description: SMS_FLAG_DESCRIPTION },
  { key: DRIVER_ROLES_FLAG, label: DRIVER_ROLES_FLAG_LABEL, description: DRIVER_ROLES_FLAG_DESCRIPTION },
  { key: TEAM_SCHEDULE_FLAG, label: TEAM_SCHEDULE_FLAG_LABEL, description: TEAM_SCHEDULE_FLAG_DESCRIPTION },
  { key: HIRE_CHECKLIST_FLAG, label: HIRE_CHECKLIST_FLAG_LABEL, description: HIRE_CHECKLIST_FLAG_DESCRIPTION },
];

/** Given a flag's global state and (optional) org-level override, what should render? */
export function computeEffectiveFlag(state: FeatureFlagState | undefined, orgOverride: boolean | undefined): boolean {
  if (state === 'off' || state === undefined) return false;
  if (state === 'on') return orgOverride !== false;
  if (state === 'rollout') return orgOverride === true;
  return false;
}

export function listenGlobalFeatureFlags(
  onChange: (flags: Record<string, FeatureFlagDef>) => void,
  onError?: (err: unknown) => void
): Unsubscribe {
  const firestoreDb = ensureDb();
  return onSnapshot(
    doc(firestoreDb, ...GLOBAL_DOC_PATH),
    (snap) => {
      const data = snap.data() as GlobalFeatureFlagsDoc | undefined;
      onChange(data?.flags ?? {});
    },
    (err) => onError?.(err)
  );
}

export function listenOrgFeatureFlagOverrides(
  orgId: string,
  onChange: (overrides: Record<string, boolean>) => void,
  onError?: (err: unknown) => void
): Unsubscribe {
  const firestoreDb = ensureDb();
  return onSnapshot(
    doc(firestoreDb, 'featureFlagOverrides', orgId),
    (snap) => {
      const data = snap.data() as OrgFeatureFlagOverridesDoc | undefined;
      onChange(data?.overrides ?? {});
    },
    (err) => onError?.(err)
  );
}

export async function getGlobalFeatureFlags(): Promise<Record<string, FeatureFlagDef>> {
  const firestoreDb = ensureDb();
  const snap = await getDoc(doc(firestoreDb, ...GLOBAL_DOC_PATH));
  const data = snap.data() as GlobalFeatureFlagsDoc | undefined;
  return data?.flags ?? {};
}

export async function getOrgFeatureFlagOverrides(orgId: string): Promise<Record<string, boolean>> {
  const firestoreDb = ensureDb();
  const snap = await getDoc(doc(firestoreDb, 'featureFlagOverrides', orgId));
  const data = snap.data() as OrgFeatureFlagOverridesDoc | undefined;
  return data?.overrides ?? {};
}

/** Super-admin only (enforced by firestore.rules isSuperAdmin()). Creates the flag if it doesn't exist yet. */
export async function upsertGlobalFlag(
  key: string,
  def: Pick<FeatureFlagDef, 'label' | 'description' | 'state'>,
  updatedByUid: string
): Promise<void> {
  const firestoreDb = ensureDb();
  await setDoc(
    doc(firestoreDb, ...GLOBAL_DOC_PATH),
    {
      flags: {
        [key]: {
          ...def,
          updatedAt: serverTimestamp(),
          updatedBy: updatedByUid,
        },
      },
    },
    { merge: true }
  );
}

/** Super-admin only. Removes a flag definition entirely from the global doc. */
export async function deleteGlobalFlag(key: string): Promise<void> {
  const firestoreDb = ensureDb();
  await updateDoc(doc(firestoreDb, ...GLOBAL_DOC_PATH), {
    [`flags.${key}`]: deleteField(),
  });
}

/** Super-admin only. Pass `value: null` to clear the override (org falls back to the global state). */
export async function setOrgFlagOverride(
  orgId: string,
  key: string,
  value: boolean | null,
  updatedByUid: string
): Promise<void> {
  const firestoreDb = ensureDb();
  const ref = doc(firestoreDb, 'featureFlagOverrides', orgId);
  if (value === null) {
    await updateDoc(ref, { [`overrides.${key}`]: deleteField() }).catch(async (err) => {
      // Doc may not exist yet — nothing to clear.
      if ((err as { code?: string })?.code !== 'not-found') throw err;
    });
    return;
  }
  await setDoc(
    ref,
    {
      overrides: { [key]: value },
      updatedAt: serverTimestamp(),
      updatedBy: updatedByUid,
    },
    { merge: true }
  );
}
